import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageTransition, Spinner } from '@/components/motion';
import BacktestConfigPanel from '../components/Backtesting/BacktestConfigPanel';
import BacktestResults from '../components/Backtesting/BacktestResults';
import useBacktest from '../backtest/hooks/useBacktest';
import { strategiesAPI } from '../services/api';

function Backtesting() {
  const [searchParams] = useSearchParams();
  const [selectedStrategyId, setSelectedStrategyId] = useState(searchParams.get('strategy') || '');
  const [formData, setFormData] = useState({
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    initialCapital: 10000,
    timeframe: '1Day',
    feeMode: 'fixed',
    makerFee: 0,
    takerFee: 0,
    overlays: ['ema20', 'volume'],
  });

  const { run, cancel, results, progress, isRunning, error } = useBacktest();

  const { data: strategiesData, isLoading: strategiesLoading } = useQuery({
    queryKey: ['strategies'],
    queryFn: () => strategiesAPI.getAll(),
  });

  const strategies = strategiesData?.data || [];
  const selectedStrategy = strategies.find((s) => s.id === selectedStrategyId);

  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleRun = useCallback(async () => {
    if (!selectedStrategyId) {
      toast.error('Select a strategy first');
      return;
    }
    try {
      await run({
        strategyId: selectedStrategyId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        initialCapital: formData.initialCapital,
        timeframe: formData.timeframe,
        feeMode: formData.feeMode || 'fixed',
        makerFee: formData.makerFee || 0,
        takerFee: formData.takerFee || 0,
      });
    } catch (err) {
      toast.error(err.message || 'Backtest failed');
    }
  }, [selectedStrategyId, formData, run]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  if (strategiesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="h-12 w-12" />
      </div>
    );
  }

  return (
    <PageTransition className="h-full flex flex-col p-3 sm:p-4 lg:p-6 gap-3 lg:overflow-hidden">
      {/* Config bar — fixed height, scrolls horizontally on mobile */}
      <div className="rounded-xl border border-white/8 bg-card/40 backdrop-blur-md text-card-foreground shadow-sm px-4 py-3 flex-shrink-0 overflow-x-auto">
        <BacktestConfigPanel
          strategies={strategies}
          selectedStrategyId={selectedStrategyId}
          onStrategyChange={setSelectedStrategyId}
          formData={formData}
          onFormChange={handleFormChange}
          onRun={handleRun}
          isRunning={isRunning}
          progress={progress}
        />
      </div>

      {/* Results — no scroll on desktop, vertical scroll handled inside on mobile */}
      <div className="flex-1 min-h-0">
        <BacktestResults
          results={results}
          initialCapital={formData.initialCapital}
          strategy={selectedStrategy}
          overlays={formData.overlays}
        />
      </div>
    </PageTransition>
  );
}

export default Backtesting;
